// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */

// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Capacitor } from "@capacitor/core";
import { BottomSheetLoadingContent } from "@clipboard-health/ui-components";
import { logError } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import {
  addAppResumeListener,
  type AppListenerHandle,
} from "@src/appV2/NativeAdapters/app.adapter";
import { DeviceNFCCapabilityForShift, startNFCScan } from "@src/appV2/Nfc/utils/nfcReader";
import { type NfcTagRequest } from "@src/appV2/Shifts/Shift/types";
import { USER_EVENTS } from "@src/constants";
import { useEffect, useRef, useState } from "react";

import { AcceptNonInstantPayContent } from "../TimekeepingActions/AcceptNonInstantPayContent";
import { NfcValidationError } from "./api/NfcValidationError";
import { type NfcHashValidationAction } from "./api/types";
import { useNfcTimekeepingScan } from "./api/useNfcTimekeepingScan";
import { type CommonNfcScanEventBase, sendNfcScanEvent } from "./nfcEvents";
import { BeginScanStage } from "./Stages/BeginScan";
import { ScanFailedStage } from "./Stages/ScanFailed";
import { ScanInstructionsStage } from "./Stages/ScanInstructions";
import { Scanner } from "./Stages/Scanner";
import { ReadingFailureType } from "./types";

interface WorkerNfcValidationProps {
  shiftId: string;
  metaProps: {
    workerId: string;
    workplaceId: string;
    workplaceName: string;
  };
  nfcTagRequests: NfcTagRequest[];
  clockAction: NfcHashValidationAction;
  disableSkip?: boolean;
  isMissedPunchRequestEnabled: boolean;

  // Final states that will close the dialog
  onSuccess: () => void;
  onSkipLocationCheck: () => void;
  onCancel: () => void;
  onNfcNotSupported: () => void;
}

const LENGTHY_VERIFICATION_THRESHOLD_IN_MS = 200;

type ScanningStage =
  | "verify-capability"
  | "begin-scan"
  | "scanner"
  | "instructions"
  | "failure"
  | "accept-non-instant-pay";
export function WorkerNfcValidation(props: WorkerNfcValidationProps) {
  const {
    shiftId,
    metaProps: { workplaceName, workplaceId, workerId },
    nfcTagRequests,
    clockAction,
    disableSkip,
    isMissedPunchRequestEnabled,
    onCancel,
    onSuccess,
    onSkipLocationCheck,
    onNfcNotSupported,
  } = props;

  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const { mutateAsync: startNfcScan } = useNfcTimekeepingScan();
  const scanStartTimeRef = useRef(Date.now());
  const [currentAttemptNumber, setCurrentAttemptNumber] = useState(0);
  const [currentScanStage, setCurrentScanStage] = useState<ScanningStage>("verify-capability");
  const [readingFailureError, setReadingFailureError] = useState<{
    type: ReadingFailureType;
    code: string | undefined;
  }>();
  const [isCapabilityVerificationLengthy, setIsCapabilityVerificationLengthy] = useState(false);

  useEffect(() => {
    async function verifyNfcCapability() {
      let isNfcSupported = false;
      if (Capacitor.isNativePlatform()) {
        const nfcReader = startNFCScan();
        const status = await nfcReader.getNfcStatus();
        isNfcSupported = [
          DeviceNFCCapabilityForShift.NFC_ENABLED,

          // Present but disabled, we still want to show the NFC scanner so user can enable it
          DeviceNFCCapabilityForShift.NFC_DISABLED,
        ].includes(status);
      }

      if (isNfcSupported) {
        setCurrentScanStage("begin-scan");
      } else {
        onNfcNotSupported();
        setCurrentScanStage("failure");
      }
    }

    if (currentScanStage === "verify-capability") {
      void verifyNfcCapability();
    }
  }, [currentScanStage, shiftId, workerId, onNfcNotSupported]);

  useEffect(() => {
    if (currentScanStage !== "verify-capability") {
      setIsCapabilityVerificationLengthy(false);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setIsCapabilityVerificationLengthy(true);
    }, LENGTHY_VERIFICATION_THRESHOLD_IN_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [currentScanStage]);

  const trackScanAttempt = (attemptNumber: number) => {
    setCurrentAttemptNumber(attemptNumber);
    scanStartTimeRef.current = Date.now();
  };

  const getScanDurationInSeconds = (): number => {
    return (Date.now() - scanStartTimeRef.current) / 1000;
  };

  const getCommonScanEventProps = (attemptNumber: number): CommonNfcScanEventBase => ({
    shiftId,
    agentId: workerId,
    facilityId: workplaceId,
    shiftClockAction: clockAction,
    attemptNumber,
  });

  function abortScan() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = undefined;
    }
  }

  // Abort the scan when the component unmounts
  useEffect(() => {
    return () => {
      abortScan();
    };
  }, []);

  // When showing the NFC disabled failure, listen for app resume to auto-retry
  useEffect(() => {
    if (
      currentScanStage !== "failure" ||
      readingFailureError?.type !== ReadingFailureType.NFC_DISABLED
    ) {
      return undefined;
    }

    let listenerHandle: AppListenerHandle | undefined;
    let cancelled = false;

    async function setupResumeListener() {
      try {
        listenerHandle = await addAppResumeListener(() => {
          if (cancelled) {
            return;
          }

          setCurrentScanStage("begin-scan");
        });
      } catch (error) {
        if (!cancelled) {
          logError(APP_V2_APP_EVENTS.NFC_ATTACH_RESUME_LISTENER_FAILED, { error });
        }
      }
    }

    void setupResumeListener();

    return () => {
      cancelled = true;
      void listenerHandle?.remove();
    };
  }, [currentScanStage, readingFailureError?.type]);

  async function performNfcScan() {
    // Create a new AbortController for this scan
    abortControllerRef.current = new AbortController();

    try {
      await startNfcScan({
        shiftId,
        clockAction,
        abortSignal: abortControllerRef.current.signal,
      });

      onScanSuccess();
    } catch (error) {
      if (error instanceof NfcValidationError) {
        // If the scan was cancelled, don't show an error
        if (error.type === ReadingFailureType.SCAN_CANCEL) {
          onScanCancelled();
          return;
        }

        onScanFailure(error.type, error.code);
        return;
      }

      onScanFailure(ReadingFailureType.OTHER, "UNKNOWN_ERROR");
    } finally {
      abortControllerRef.current = undefined;
    }
  }

  function onScanStarted() {
    const newAttemptNumber = currentAttemptNumber + 1;
    trackScanAttempt(newAttemptNumber);

    sendNfcScanEvent({
      type: USER_EVENTS.NFC_SCAN_START,
      data: {
        ...getCommonScanEventProps(newAttemptNumber),
      },
    });

    setCurrentScanStage("scanner");
    void performNfcScan();
  }

  function onScanSuccess() {
    sendNfcScanEvent({
      type: USER_EVENTS.NFC_SCAN_SUCCEEDED,
      data: {
        ...getCommonScanEventProps(currentAttemptNumber),
        scanDuration: getScanDurationInSeconds(),
      },
    });

    onSuccess();
  }

  function onScanFailure(failureType: ReadingFailureType, errorCode?: string) {
    setReadingFailureError({
      type: failureType,
      code: errorCode,
    });

    sendNfcScanEvent({
      type: USER_EVENTS.NFC_SCAN_FAILED,
      data: {
        ...getCommonScanEventProps(currentAttemptNumber),
        scanDuration: getScanDurationInSeconds(),
        failureReason: failureType,
        errorCode,
      },
    });
    setCurrentScanStage("failure");
  }

  function onScanCancelled() {
    // Abort the ongoing scan if there is one
    abortScan();

    sendNfcScanEvent({
      type: USER_EVENTS.NFC_SCAN_CANCELLED,
      data: {
        ...getCommonScanEventProps(currentAttemptNumber),
        scanDuration: getScanDurationInSeconds(),
      },
    });
    setCurrentScanStage("begin-scan");
  }

  return (
    <>
      {currentScanStage === "verify-capability" && isCapabilityVerificationLengthy && (
        <BottomSheetLoadingContent
          title="Verifying your device's NFC"
          description="Should only take a few seconds"
        />
      )}

      {currentScanStage === "begin-scan" && (
        <BeginScanStage
          clockAction={clockAction}
          workplaceName={workplaceName}
          nfcTagRequests={nfcTagRequests}
          onStartScan={() => {
            onScanStarted();
          }}
          onCancel={() => {
            onCancel();
          }}
          onHelpScanningPoster={() => {
            setCurrentScanStage("instructions");
          }}
        />
      )}

      {currentScanStage === "scanner" && (
        <Scanner
          onScanCancelled={() => {
            onScanCancelled();
          }}
        />
      )}

      {currentScanStage === "instructions" && (
        <ScanInstructionsStage
          shiftId={shiftId}
          workplaceName={workplaceName}
          nfcTagRequests={nfcTagRequests}
          clockAction={clockAction}
          onStartScan={() => {
            onScanStarted();
          }}
          onCancel={() => {
            setCurrentScanStage("begin-scan");
          }}
        />
      )}

      {currentScanStage === "failure" && (
        <ScanFailedStage
          workplaceName={workplaceName}
          nfcTagRequests={nfcTagRequests}
          failureType={readingFailureError?.type}
          errorCode={readingFailureError?.code}
          attemptNumber={currentAttemptNumber}
          disableSkip={disableSkip}
          onBack={() => {
            setCurrentScanStage("begin-scan");
          }}
          onSkipVerification={() => {
            setCurrentScanStage("accept-non-instant-pay");
          }}
          onHelpClick={() => {
            setCurrentScanStage("instructions");
          }}
        />
      )}

      {currentScanStage === "accept-non-instant-pay" && (
        <AcceptNonInstantPayContent
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          onAccept={() => {
            setCurrentScanStage("begin-scan");
            onSkipLocationCheck();
          }}
          onCancel={() => {
            setCurrentScanStage("begin-scan");
          }}
        />
      )}
    </>
  );
}
